import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createFileRoute,
  Outlet,
  redirect,
  useRouterState,
  type ErrorComponentProps,
} from '@tanstack/react-router';
import { useEffect } from 'react';

import { AppCrashPage } from '@/app/AppCrashPage';
import { AppShell } from '@/app/layout/AppShell';
import { ApiUnavailablePage } from '@/features/auth/ApiUnavailablePage';
import { logout } from '@/features/auth/api';
import { meQueryOptions } from '@/features/auth/session';
import { ApiProblemError } from '@/shared/api/problem';

/**
 * Проверка сессии не удалась: `/me` не ответил или ответил не 401.
 *
 * Обёртка нужна, чтобы errorComponent отличал недоступный API от падения внутри самой панели.
 * Он ловит ошибки всех вложенных разделов, а не только `beforeLoad`, и без метки исключение
 * в любом экране показывалось как «Админка недоступна» — то есть уводило разбираться в бэкенд.
 */
class SessionCheckFailedError extends Error {
  /** Исходная ошибка: по ней экран отличает отказ сервера от молчания сети. */
  readonly reason: unknown;

  constructor(reason: unknown) {
    super('Не удалось проверить сессию сотрудника');
    this.name = 'SessionCheckFailedError';
    this.reason = reason;
  }
}

/**
 * Разделы админки за сессией сотрудника (ADR-0002).
 *
 * Маршрут беспутевой (`_app`) — адреса разделов не меняются, но все они получают общую
 * проверку до отрисовки: без действующей сессии сотрудник уходит на вход, а с непогашенным
 * требованием сменить пароль — на смену пароля. Отрисовать раздел и получить 403 на каждом
 * его запросе было бы хуже: экран мигал бы содержимым, работать с которым всё равно нельзя.
 */
export const Route = createFileRoute('/_app')({
  beforeLoad: async ({ context, location }) => {
    const member = await context.queryClient.ensureQueryData(meQueryOptions).catch((error: unknown) => {
      if (error instanceof ApiProblemError && error.status === 401) {
        throw redirect({ to: '/login', search: { redirect: location.href } });
      }
      // Лежащий бэкенд — не повод показывать форму входа: она примет пароль и упрётся
      // в ту же ошибку. Пусть отработает errorComponent с честным «API недоступен».
      throw new SessionCheckFailedError(error);
    });

    if (member.mustChangePassword) {
      throw redirect({ to: '/change-password' });
    }
    return { member };
  },
  component: AppLayout,
  errorComponent: AppError,
});

function AppLayout() {
  const { member: loaded } = Route.useRouteContext();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const href = useRouterState({ select: (state) => state.location.href });

  // Данные уже в кэше — запрос из beforeLoad. Подписка нужна ради обновлений: рефетч по
  // возврату в окно заметит и смену имени, и истёкшую сессию.
  const { data: member = loaded, error } = useQuery(meQueryOptions);

  const sessionExpired = error instanceof ApiProblemError && error.status === 401;
  useEffect(() => {
    if (sessionExpired) {
      void navigate({ to: '/login', search: { redirect: href } });
    }
  }, [sessionExpired, navigate, href]);

  const logoutMutation = useMutation({
    mutationFn: logout,
    onSuccess: () => {
      // Чужие данные в кэше после смены сотрудника — источник чужих цифр на экране.
      queryClient.clear();
      void navigate({ to: '/login' });
    },
  });

  return (
    <AppShell
      member={member}
      onLogout={() => logoutMutation.mutate()}
      logoutPending={logoutMutation.isPending}
      logoutFailed={logoutMutation.isError}
    >
      <Outlet />
    </AppShell>
  );
}

function AppError({ error, reset }: ErrorComponentProps) {
  if (error instanceof SessionCheckFailedError) {
    return <ApiUnavailablePage error={error.reason} onRetry={reset} />;
  }
  // Всё остальное сюда приносят разделы: исключение в коде экрана или в его библиотеке.
  return <AppCrashPage error={error} onRetry={reset} />;
}
